// F-071 : registre des clés et accès physiques. Les codes ne sont visibles que des membres qui opèrent.
import { useState } from "react";
import { patch, post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime, money } from "@core/format";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Table, fieldError } from "@core/ui";
import { KEY_HOLDERS, KEY_KINDS, KEY_MOVEMENTS, KEY_STATUS, LeaseSelect, PropertySelect, Status, meLease, options, orgApi, todayIso, useAction } from "./shared";

function Code({ value }: { value: string | null | undefined }) {
  const [shown, setShown] = useState(false);
  if (!value) return <span className="muted">—</span>;
  return (
    <span className="row">
      <span className="vie-code" aria-label={shown ? `Code ${value}` : "Code masqué"}>
        {shown ? value : "••••"}
      </span>
      <button type="button" className="vie-link small" onClick={() => setShown(!shown)}>
        {shown ? "Masquer" : "Afficher"}
      </button>
    </span>
  );
}

function MovementModal({ orgId, keyRow, onClose }: { orgId: string; keyRow: any; onClose: () => void }) {
  const [form, setForm] = useState({ kind: keyRow.status === "remise" ? "restitution" : "remise", movement_on: todayIso(), holder_kind: "locataire", holder_label: "", lease_id: "", note: "", cost_cents: null as number | null });
  const act = useAction();
  const invalidate = useInvalidate();
  return (
    <Modal
      open
      onClose={onClose}
      title={`Mouvement : ${keyRow.label}`}
      footer={
        <Button
          busy={act.busy}
          onClick={async () => {
            const payload: any = { kind: form.kind, movement_on: form.movement_on, note: form.note, cost_cents: form.cost_cents ?? undefined };
            if (form.kind === "remise") Object.assign(payload, { holder_kind: form.holder_kind, holder_label: form.holder_label, lease_id: form.lease_id || undefined });
            if (await act.run(() => post(orgApi(orgId, `/keys/${keyRow.id}/movements`), payload), "Mouvement enregistré.")) {
              invalidate(orgApi(orgId, "/keys"));
              onClose();
            }
          }}
        >
          Enregistrer
        </Button>
      }
    >
      <div className="stack">
        <ErrorBox error={act.error} />
        <Select
          label="Mouvement"
          value={form.kind}
          options={options({ remise: "Remise", restitution: "Restitution", perte: "Perte", remplacement: "Remplacement", desactivation: "Désactivation" })}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
        />
        <Input label="Date" type="date" value={form.movement_on} error={fieldError(act.error, "movement_on")} onChange={(e) => setForm({ ...form, movement_on: e.target.value })} />
        {form.kind === "remise" ? (
          <>
            <Select label="Remis à" value={form.holder_kind} options={options({ locataire: "Locataire", tiers: "Tiers", gardien: "Gardien", syndic: "Syndic", artisan: "Artisan" })} onChange={(e) => setForm({ ...form, holder_kind: e.target.value })} />
            {form.holder_kind === "locataire" ? <LeaseSelect orgId={orgId} value={form.lease_id} error={fieldError(act.error, "lease_id")} onChange={(v) => setForm({ ...form, lease_id: v })} /> : null}
            <Input label="Nom du détenteur" value={form.holder_label} error={fieldError(act.error, "holder_label")} onChange={(e) => setForm({ ...form, holder_label: e.target.value })} help={form.holder_kind === "locataire" ? "Laissez vide pour reprendre le nom du locataire." : undefined} />
          </>
        ) : null}
        {["perte", "remplacement"].includes(form.kind) ? <MoneyInput label="Coût éventuel" cents={form.cost_cents} onChange={(c) => setForm({ ...form, cost_cents: c })} /> : null}
        <Input label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <p className="small muted">Ne notez pas de code d’accès ici : il resterait visible dans l’historique.</p>
      </div>
    </Modal>
  );
}

function History({ orgId, id, onClose }: { orgId: string; id: string; onClose: () => void }) {
  const q = useGet<any>(orgApi(orgId, `/keys/${id}`));
  return (
    <Modal open onClose={onClose} title="Historique de l’exemplaire">
      {!q.data ? (
        <Loading />
      ) : (
        <Table caption="Mouvements">
          <thead>
            <tr>
              <th>Date</th>
              <th>Mouvement</th>
              <th>De → à</th>
              <th>Confirmation</th>
            </tr>
          </thead>
          <tbody>
            {q.data.movements.map((m: any) => (
              <tr key={m.id}>
                <td className="num">{date(m.movement_on)}</td>
                <td>
                  {KEY_MOVEMENTS[m.kind]}
                  {m.cost_cents != null ? <span className="block small">{money(m.cost_cents)}</span> : null}
                  {m.note ? <span className="block small muted">{m.note}</span> : null}
                </td>
                <td className="small">
                  {m.from_label || "—"} → {m.to_label || "—"}
                </td>
                <td className="small">{m.confirmed_by_holder_at ? `Reçue le ${dateTime(m.confirmed_by_holder_at)}` : m.kind === "remise" ? "Non confirmée" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Modal>
  );
}

function KeysTable({ orgId, propertyId }: { orgId: string; propertyId?: string }) {
  const org = useOrg();
  const path = orgApi(orgId, `/keys${propertyId ? `?property_id=${propertyId}` : ""}`);
  const q = useGet<{ items: any[]; codes_visible: boolean }>(path);
  const invalidate = useInvalidate();
  const [form, setForm] = useState({ property_id: propertyId ?? "", kind: "cle", label: "", reference: "", access_code: "", common_area: false });
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState<any>(null);
  const [history, setHistory] = useState("");
  const [editCode, setEditCode] = useState<{ id: string; code: string } | null>(null);
  const act = useAction();
  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted">Chaque exemplaire a son détenteur et son historique. Les codes d’accès ne figurent jamais dans les envois ni dans les paquets de passation.</p>
        {org.can("operate") ? (
          <Button variant="secondaire" onClick={() => setAdding(!adding)}>
            {adding ? "Fermer" : "Ajouter un exemplaire"}
          </Button>
        ) : null}
      </div>
      <ErrorBox error={act.error} />
      {adding ? (
        <Card title="Nouvel exemplaire">
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await act.run(() => post(orgApi(orgId, "/keys"), { ...form, access_code: form.access_code || undefined }), "Exemplaire ajouté.")) {
                setForm({ ...form, label: "", reference: "", access_code: "" });
                setAdding(false);
                invalidate(orgApi(orgId, "/keys"));
              }
            }}
          >
            {!propertyId ? <PropertySelect orgId={orgId} value={form.property_id} error={fieldError(act.error, "property_id")} onChange={(v) => setForm({ ...form, property_id: v })} /> : null}
            <div className="form-row">
              <Select label="Type" value={form.kind} options={options(KEY_KINDS)} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
              <Input label="Libellé" required value={form.label} error={fieldError(act.error, "label")} placeholder="Ex. Clé porte d’entrée n° 2" onChange={(e) => setForm({ ...form, label: e.target.value })} />
              <Input label="Référence" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
            </div>
            <Input label="Code d’accès (facultatif)" autoComplete="off" value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value })} help="Visible uniquement des membres qui peuvent opérer dans l’espace." />
            <Checkbox label="Accès commun de l’immeuble" checked={form.common_area} onChange={(v) => setForm({ ...form, common_area: v })} />
            <div>
              <Button type="submit" busy={act.busy}>
                Ajouter
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
      {q.isLoading ? (
        <Loading />
      ) : q.data?.items.length ? (
        <Table caption="Registre des clés">
          <thead>
            <tr>
              <th>Exemplaire</th>
              {!propertyId ? <th>Logement</th> : null}
              <th>Statut</th>
              <th>Détenteur</th>
              <th>Code</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((k) => (
              <tr key={k.id}>
                <td>
                  {k.label}
                  <span className="block small muted">
                    {KEY_KINDS[k.kind]}
                    {k.reference ? ` · ${k.reference}` : ""}
                    {k.common_area ? " · accès commun" : ""}
                  </span>
                </td>
                {!propertyId ? <td>{k.property_label}</td> : null}
                <td>
                  <Status value={k.status} labels={KEY_STATUS} />
                </td>
                <td>{k.status === "remise" || k.status === "perdue" ? `${k.holder_label} (${KEY_HOLDERS[k.holder_kind]})` : "Bailleur"}</td>
                <td>{q.data!.codes_visible ? <Code value={k.access_code} /> : k.has_code ? <span className="small muted">Code enregistré, non visible pour votre rôle</span> : "—"}</td>
                <td>
                  <div className="row">
                    {org.can("operate") && k.status !== "desactivee" ? (
                      <Button small variant="secondaire" onClick={() => setMoving(k)}>
                        Mouvement
                      </Button>
                    ) : null}
                    {org.can("operate") ? (
                      <Button small variant="discret" onClick={() => setEditCode({ id: k.id, code: k.access_code ?? "" })}>
                        Code
                      </Button>
                    ) : null}
                    <Button small variant="discret" onClick={() => setHistory(k.id)}>
                      Historique
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Empty title="Aucun exemplaire enregistré">Inventoriez clés, badges et télécommandes pour savoir qui détient quoi.</Empty>
      )}
      {moving ? <MovementModal orgId={orgId} keyRow={moving} onClose={() => setMoving(null)} /> : null}
      {history ? <History orgId={orgId} id={history} onClose={() => setHistory("")} /> : null}
      {editCode ? (
        <Modal
          open
          onClose={() => setEditCode(null)}
          title="Code d’accès"
          footer={
            <Button
              busy={act.busy}
              onClick={async () => {
                if (await act.run(() => patch(orgApi(orgId, `/keys/${editCode.id}`), { access_code: editCode.code }), "Code enregistré.")) {
                  setEditCode(null);
                  invalidate(orgApi(orgId, "/keys"));
                }
              }}
            >
              Enregistrer
            </Button>
          }
        >
          <Input label="Code" autoComplete="off" value={editCode.code} onChange={(e) => setEditCode({ ...editCode, code: e.target.value })} help="Laissez vide pour supprimer le code. Le changement est journalisé, sans le code." />
        </Modal>
      ) : null}
    </div>
  );
}

export function KeysPage() {
  const org = useOrg();
  return (
    <div className="page">
      <PageHeader eyebrow="Vie du logement" title="Clés et accès" intro="Clés, badges, télécommandes : détenteurs, remises et restitutions datées." />
      <KeysTable orgId={org.orgId} />
    </div>
  );
}

/** Onglet « Clés » de la fiche logement. */
export function PropertyKeysTab({ orgId, propertyId }: { orgId: string; propertyId: string }) {
  return <KeysTable orgId={orgId} propertyId={propertyId} />;
}

/** Clés remises au locataire, à confirmer (jamais de code affiché). */
export function TenantKeys({ leaseId }: { leaseId: string }) {
  const path = meLease(leaseId, "/keys");
  const q = useGet<{ items: any[] }>(path);
  const invalidate = useInvalidate();
  const act = useAction();
  if (!q.data?.items.length) return null;
  return (
    <Card title="Clés et badges remis">
      <ErrorBox error={act.error} />
      <ul className="list-plain">
        {q.data.items.map((k) => (
          <li key={k.id} className="row-between">
            <span>
              <strong>{k.label}</strong> <span className="small muted">· {KEY_KINDS[k.kind]} · remis le {date(k.given_on)}</span>
            </span>
            {k.confirmed_at ? (
              <Status value="acceptee" label={`Réception confirmée le ${date(k.confirmed_at)}`} />
            ) : (
              <Button small variant="secondaire" onClick={async () => { await act.run(() => post(`${path}/${k.id}/confirm`, {}), "Réception confirmée."); invalidate(path); }}>
                Je confirme l’avoir reçu
              </Button>
            )}
          </li>
        ))}
      </ul>
      <Notice tone="info">Au départ, la date de remise des clés fait courir le délai de restitution du dépôt de garantie.</Notice>
    </Card>
  );
}
